/**
 * Animal Health Repositioning — AI Opportunity Score Results (v25.1 · Aug 2026)
 * ============================================================================
 * Three-tier decision-first UX. After the AI evaluation completes, assets are
 * automatically classified into three prominent, clickable tier categories:
 *   • Tier 1 – Pursue Now       (80–100)
 *   • Tier 2 – Advance / Monitor (60–79)
 *   • Tier 3 – Park / Pass       (0–59)
 * Users click a tier card OR use the persistent tabs to filter the table.
 * Every asset has a Deep Dive modal AND a Manual Committee Decision override
 * (preserves AI recommendation alongside the committee call).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Loader2, Download, ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Sparkles, ScanEye, Check, X as XIcon, LayoutList,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api/animal-health';

// ─────────────────────────────────────────────────────────────
// Tier metadata — id / label / range / description / palette.
// ─────────────────────────────────────────────────────────────
const TIER_META = {
  pursue: {
    id: 'pursue',
    label: 'Tier 1 – Pursue Now',
    range: '80–100',
    subtitle: 'Immediate detailed review, partner mapping and outreach',
    bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-900',
    chipBg: 'bg-emerald-100', chipBorder: 'border-emerald-300', chipText: 'text-emerald-800',
    dot: 'bg-emerald-500', fill: 'bg-emerald-500',
    activeBg: 'bg-emerald-600', activeText: 'text-white',
  },
  advance: {
    id: 'advance',
    label: 'Tier 2 – Advance / Monitor',
    range: '60–79',
    subtitle: 'Deeper AI review, expert assessment or additional evidence generation',
    bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900',
    chipBg: 'bg-amber-100', chipBorder: 'border-amber-300', chipText: 'text-amber-900',
    dot: 'bg-amber-500', fill: 'bg-amber-500',
    activeBg: 'bg-amber-600', activeText: 'text-white',
  },
  pass: {
    id: 'pass',
    label: 'Tier 3 – Park / Pass',
    range: '0–59',
    subtitle: 'Not prioritised unless significant new evidence emerges',
    bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-800',
    chipBg: 'bg-slate-100', chipBorder: 'border-slate-300', chipText: 'text-slate-700',
    dot: 'bg-slate-400', fill: 'bg-slate-400',
    activeBg: 'bg-slate-600', activeText: 'text-white',
  },
};

const MANUAL_DECISION_OPTIONS = [
  { id: 'not_reviewed', label: 'Not Yet Reviewed' },
  { id: 'pursue',       label: 'Pursue Now' },
  { id: 'advance',      label: 'Advance / Monitor' },
  { id: 'pass',         label: 'Park / Pass' },
];

// ─────────────────────────────────────────────────────────────
// Tier chip.
// ─────────────────────────────────────────────────────────────
const TierChip = ({ recommendation, size = 'md' }) => {
  const id = recommendation?.id || 'pass';
  const s = TIER_META[id] || TIER_META.pass;
  const sizeCls = size === 'sm' ? 'text-[10.5px] px-1.5 py-0.5' : 'text-[11.5px] px-2 py-1';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border ${s.chipBg} ${s.chipText} ${s.chipBorder} ${sizeCls} font-semibold whitespace-nowrap`}
      data-testid={`ah-tier-chip-${id}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {recommendation?.label || s.label}
    </span>
  );
};

const ConfidencePill = ({ value }) => {
  const v = String(value || 'Low');
  const cls = {
    High:   'bg-emerald-50 border-emerald-200 text-emerald-700',
    Medium: 'bg-amber-50 border-amber-200 text-amber-800',
    Low:    'bg-rose-50 border-rose-200 text-rose-700',
  }[v] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <span className={`inline-flex items-center rounded border px-1.5 text-[10px] font-semibold ${cls}`}>
      {v}
    </span>
  );
};

const ScoreBar = ({ score, tierId }) => {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const fill = TIER_META[tierId]?.fill || 'bg-slate-400';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`${fill} h-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[13px] font-bold tabular-nums text-slate-800 w-10 text-right">
        {pct}
      </span>
    </div>
  );
};

const SortHeader = ({ label, field, sort, setSort, className = '' }) => {
  const active = sort.field === field;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={`px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-slate-600 cursor-pointer hover:text-slate-900 select-none ${className}`}
      onClick={() => setSort(s => ({ field, dir: s.field === field && s.dir === 'desc' ? 'asc' : 'desc' }))}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`w-3 h-3 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
      </span>
    </th>
  );
};

// ─────────────────────────────────────────────────────────────
// Deep Dive modal — same 10-criteria breakdown, side-panel style.
// ─────────────────────────────────────────────────────────────
const DeepDiveModal = ({ open, onClose, item, criteria }) => {
  if (!item) return null;
  const critData = item.criteria || {};
  const tierId = item.recommendation?.id || 'pass';
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="ah-deep-dive-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-[18px] font-semibold text-slate-900 mb-0.5">
                {item.asset_name || 'Asset'}
              </div>
              <div className="text-[12px] text-slate-500 font-normal">
                {item.company_name || '—'}
                {item.country ? <span className="ml-1">· {item.country}</span> : null}
                {item.phase ? <span className="ml-1">· {item.phase}</span> : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">AI Score</div>
                <div className="text-[24px] font-bold text-slate-900 tabular-nums leading-none">
                  {item.ai_opportunity_score ?? 0}
                  <span className="text-[13px] text-slate-400 font-medium">/100</span>
                </div>
              </div>
              <TierChip recommendation={item.recommendation} />
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Asset context strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          {[
            ['Primary indication', item.primary_indication],
            ['Therapeutic area',   item.therapeutic_area],
            ['Modality',           item.modality],
            ['Stage',              item.phase],
          ].map(([k, v]) => (
            <div key={k} className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">{k}</div>
              <div className="text-[11.5px] text-slate-800 truncate">{v || '—'}</div>
            </div>
          ))}
        </div>

        {/* 10 criteria breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {criteria.map(c => {
            const cc = critData[c.key] || {};
            return (
              <div
                key={c.key}
                className="bg-white border border-slate-200 rounded-md p-3"
                data-testid={`ah-dd-crit-${c.key}`}
              >
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div className="text-[12px] font-semibold text-slate-800 flex-1">
                    {c.label}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[13px] font-bold text-slate-900 tabular-nums">
                      {cc.score ?? 0}<span className="text-slate-400 text-[10px] font-medium">/10</span>
                    </span>
                    <ConfidencePill value={cc.confidence} />
                  </div>
                </div>
                <p className="text-[11.5px] text-slate-600 leading-snug mb-1.5">
                  {cc.rationale || <em className="text-slate-400">No rationale returned.</em>}
                </p>
                {(cc.key_evidence || []).length > 0 && (
                  <ul className="text-[11px] text-slate-500 space-y-0.5 pl-3 list-disc">
                    {(cc.key_evidence || []).map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Strengths / Risks / Next action */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-emerald-800 font-semibold mb-1">Key strengths</div>
            {(item.key_strengths || []).length ? (
              <ul className="text-[11.5px] text-emerald-900 space-y-1 pl-3 list-disc">
                {(item.key_strengths || []).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            ) : <em className="text-[11px] text-emerald-700">—</em>}
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-rose-800 font-semibold mb-1">Key risks / evidence gaps</div>
            {(item.key_risks || []).length ? (
              <ul className="text-[11.5px] text-rose-900 space-y-1 pl-3 list-disc">
                {(item.key_risks || []).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            ) : <em className="text-[11px] text-rose-700">—</em>}
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-indigo-800 font-semibold mb-1">
              Recommended next action <span className="text-[9px] font-normal text-indigo-500">· AI</span>
            </div>
            <p className="text-[11.5px] text-indigo-900">
              {item.recommended_next_action || <em className="text-indigo-700">—</em>}
            </p>
          </div>
        </div>

        {/* Divergence indicator when committee disagrees with AI */}
        {item.manual_decision && item.manual_decision.id && item.manual_decision.id !== 'not_reviewed'
          && item.manual_decision.id !== (item.recommendation?.id) && (
          <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-md p-3 text-[11.5px] text-indigo-900">
            <span className="font-semibold">Committee decision:</span> {item.manual_decision.label}
            {item.manual_decision.reason && (
              <span className="italic ml-1">— &ldquo;{item.manual_decision.reason}&rdquo;</span>
            )}
            <span className="ml-1 text-indigo-500">(differs from AI recommendation)</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function AnimalHealthResultsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pursue: 0, advance: 0, pass: 0, total: 0 });
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [tier, setTier] = useState('all');   // all|pursue|advance|pass
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ field: 'ai_opportunity_score', dir: 'desc' });
  const [ddItem, setDdItem] = useState(null);
  const [savingDecision, setSavingDecision] = useState(null);  // asset_id being saved

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, cfg] = await Promise.all([
        axios.get(`${API}/assessment/results`, { withCredentials: true }),
        axios.get(`${API}/assessment/config`, { withCredentials: true }),
      ]);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || { pursue: 0, advance: 0, pass: 0, total: 0 });
      setCriteria(cfg.data?.criteria || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(it => {
      if (tier !== 'all' && (it.recommendation?.id || 'pass') !== tier) return false;
      if (!q) return true;
      return [it.asset_name, it.company_name, it.therapeutic_area, it.primary_indication]
        .some(v => (v || '').toLowerCase().includes(q));
    });
  }, [items, tier, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sort.field]; const bv = b[sort.field];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sort.dir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase(); const bs = String(bv).toLowerCase();
      return sort.dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return arr;
  }, [filtered, sort]);

  const setManualDecision = async (assetId, decisionId) => {
    setSavingDecision(assetId);
    try {
      const res = await axios.patch(
        `${API}/assessment/asset/${assetId}/manual-decision`,
        { decision: decisionId },
        { withCredentials: true },
      );
      setItems(list => list.map(x => x.asset_id === assetId
        ? { ...x, manual_decision: res.data?.manual_decision }
        : x,
      ));
      toast.success('Decision saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save decision');
    } finally {
      setSavingDecision(null);
    }
  };

  const exportExcel = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/assessment/results/export`, {
        withCredentials: true, responseType: 'blob',
        params: tier === 'all' ? {} : { band: tier },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      link.setAttribute('download', m ? m[1] : 'Modulus-AH-Opportunity-Scores.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch {
      toast.error('Excel export failed');
    } finally {
      setDownloading(false);
    }
  };

  const pctOf = (n) => counts.total ? Math.round(100 * n / counts.total) : 0;

  return (
    <div className="p-6 overflow-y-auto min-h-screen bg-slate-50" data-testid="ah-results-page">
      <div className="max-w-7xl mx-auto">
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h1 className="text-2xl font-semibold text-slate-900">
                AI Opportunity Score — Results
              </h1>
            </div>
            <p className="text-[13px] text-slate-600">
              {counts.total} eligible assets scored. Assets are automatically classified into three decision tiers — click a tier card or use the tabs to review each category.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/animal-health">
              <Button variant="outline" size="sm" className="h-8 text-[12px]" data-testid="ah-back-to-screening">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to screening
              </Button>
            </Link>
            <Button
              size="sm"
              onClick={exportExcel}
              disabled={downloading || sorted.length === 0}
              className="h-8 text-[12px] bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="ah-export-excel-btn"
            >
              {downloading
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Exporting…</>
                : <><Download className="w-3.5 h-3.5 mr-1" /> Export to Excel</>}
            </Button>
          </div>
        </header>

        {/* Three prominent clickable tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {['pursue', 'advance', 'pass'].map(id => {
            const meta = TIER_META[id];
            const n = counts[id] || 0;
            const selected = tier === id;
            return (
              <button
                type="button"
                key={id}
                onClick={() => setTier(id)}
                className={`text-left border-2 rounded-lg p-4 transition-all hover:shadow-sm ${
                  selected
                    ? `${meta.bg} ${meta.border} ring-2 ring-offset-1 ring-${id === 'pursue' ? 'emerald' : id === 'advance' ? 'amber' : 'slate'}-400`
                    : `bg-white ${meta.border} hover:${meta.bg}`
                }`}
                data-testid={`ah-tier-card-${id}`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className={`text-[13px] font-bold ${meta.text}`}>
                    {meta.label}
                  </div>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.chipBg} ${meta.chipText}`}>
                    {meta.range}
                  </span>
                </div>
                <div className={`text-[32px] font-bold tabular-nums leading-tight ${meta.text}`}>
                  {n}
                  <span className="text-[13px] font-medium text-slate-500 ml-1.5">
                    asset{n === 1 ? '' : 's'} · {pctOf(n)}%
                  </span>
                </div>
                <p className={`text-[11px] mt-1 ${meta.text} opacity-80`}>
                  {meta.subtitle}
                </p>
              </button>
            );
          })}
        </div>

        {/* Tabs + search */}
        <div className="bg-white border border-slate-200 rounded-lg p-2 mb-4 flex flex-wrap items-center gap-1">
          {[
            { id: 'all', label: `All (${counts.total || 0})` },
            { id: 'pursue', label: `Pursue Now (${counts.pursue || 0})` },
            { id: 'advance', label: `Advance / Monitor (${counts.advance || 0})` },
            { id: 'pass', label: `Park / Pass (${counts.pass || 0})` },
          ].map(t => {
            const active = tier === t.id;
            const meta = TIER_META[t.id];
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => setTier(t.id)}
                className={`px-3 py-1.5 rounded text-[11.5px] font-semibold transition-colors ${
                  active
                    ? (meta ? `${meta.activeBg} ${meta.activeText}` : 'bg-indigo-600 text-white')
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`ah-tab-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search asset / company / TA…"
              className="h-8 text-[12px] w-[280px]"
              data-testid="ah-results-search"
            />
          </div>
        </div>

        {/* Results table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-slate-500 text-[13px]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading assessments…
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-10 text-center">
              <LayoutList className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-[14px] text-slate-700 mb-1">
                {counts.total === 0 ? 'No scored assets yet' : 'No assets in this tier'}
              </p>
              <p className="text-[12px] text-slate-500 mb-4">
                {counts.total === 0
                  ? 'Head to the screening page and click Run AI evaluation to score the current eligible cohort.'
                  : 'Try a different tier or clear the search.'}
              </p>
              {counts.total === 0 && (
                <Link to="/animal-health">
                  <Button size="sm" variant="outline" className="h-8 text-[12px]">
                    <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to screening
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <table className="w-full text-[12px]" data-testid="ah-results-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <SortHeader label="Asset" field="asset_name" sort={sort} setSort={setSort} />
                  <SortHeader label="Company" field="company_name" sort={sort} setSort={setSort} />
                  <SortHeader label="Phase" field="phase" sort={sort} setSort={setSort} />
                  <SortHeader label="TA / Indication" field="therapeutic_area" sort={sort} setSort={setSort} />
                  <SortHeader label="AI Score" field="ai_opportunity_score" sort={sort} setSort={setSort} className="w-[180px]" />
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-slate-600">
                    AI Recommendation
                  </th>
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-slate-600 w-[200px]">
                    Committee Decision
                  </th>
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-slate-600 w-[110px]">
                    Deep Dive
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(item => {
                  const tierId = item.recommendation?.id || 'pass';
                  const manId = item.manual_decision?.id || 'not_reviewed';
                  const divergent = manId !== 'not_reviewed' && manId !== tierId;
                  return (
                    <tr
                      key={item.asset_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`ah-results-row-${item.asset_id}`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-900 truncate max-w-[240px]">
                        {item.asset_name || '—'}
                        {item.primary_indication && (
                          <div className="text-[10.5px] text-slate-500 truncate">{item.primary_indication}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700 truncate max-w-[200px]">
                        {item.company_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{item.phase || '—'}</td>
                      <td className="px-3 py-2 text-slate-700 truncate max-w-[180px]">
                        {item.therapeutic_area || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <ScoreBar score={item.ai_opportunity_score} tierId={tierId} />
                      </td>
                      <td className="px-3 py-2">
                        <TierChip recommendation={item.recommendation} size="sm" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Select
                            value={manId}
                            onValueChange={(v) => setManualDecision(item.asset_id, v)}
                            disabled={savingDecision === item.asset_id}
                          >
                            <SelectTrigger
                              className={`h-7 text-[11px] px-2 ${divergent ? 'border-indigo-400 bg-indigo-50' : ''}`}
                              data-testid={`ah-manual-decision-${item.asset_id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {MANUAL_DECISION_OPTIONS.map(o => (
                                <SelectItem key={o.id} value={o.id} className="text-[12px]">
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {savingDecision === item.asset_id && (
                            <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                          )}
                          {divergent && !savingDecision && (
                            <span
                              title="Differs from AI recommendation — both values retained"
                              className="text-[9.5px] text-indigo-600 font-semibold"
                            >
                              ≠ AI
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDdItem(item)}
                          className="h-7 text-[11px]"
                          data-testid={`ah-deep-dive-${item.asset_id}`}
                        >
                          <ScanEye className="w-3 h-3 mr-1" /> Deep Dive
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <DeepDiveModal
        open={!!ddItem}
        onClose={() => setDdItem(null)}
        item={ddItem}
        criteria={criteria}
      />
    </div>
  );
}
